'use client';

import React, { FormEvent, useState } from 'react';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Globe2,
  Lock,
  Mail,
  UserRound,
} from 'lucide-react';

interface CompanyRegistrationFormProps {
  onBack: () => void;
}

interface RegistrationCategory {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  orden: number;
}

const inputClass =
  'w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10';

const labelClass =
  'mb-1.5 block text-sm font-700 text-foreground';

export default function CompanyRegistrationForm({
  onBack,
}: CompanyRegistrationFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<RegistrationCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',

    razonSocial: '',
    nombreComercial: '',
    rfc: '',
    sector: '',
    categoryId: '',
    tamano: '',
    anioConstitucion: '',
    sitioWeb: '',

    domicilio: '',
    estado: '',
    municipio: '',
    codigoPostal: '',
    pais: 'México',

    nombreCompleto: '',
    cargo: '',
    telefono: '',

    infoExportaciones: '',
    paisesDestino: '',
    productosExportados: '',
  });

  React.useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      try {
        const response = await fetch('/api/registration/categories', {
          method: 'GET',
          cache: 'no-store',
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.error || 'No fue posible cargar las categorías.'
          );
        }

        if (!cancelled) {
          setCategories(data?.categories || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'No fue posible cargar las categorías.'
          );
        }
      } finally {
        if (!cancelled) {
          setCategoriesLoading(false);
        }
      }
    }

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  function update(
    field: keyof typeof form,
    value: string
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/registration/company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: form.email,
          password: form.password,

          razonSocial: form.razonSocial,
          nombreComercial: form.nombreComercial,
          rfc: form.rfc,
          sector: form.sector,
          categoryId: form.categoryId,
          tamano: form.tamano,
          anioConstitucion: form.anioConstitucion,
          sitioWeb: form.sitioWeb,

          domicilio: form.domicilio,
          estado: form.estado,
          municipio: form.municipio,
          codigoPostal: form.codigoPostal,
          pais: form.pais,

          nombreCompleto: form.nombreCompleto,
          cargo: form.cargo,
          telefono: form.telefono,

          infoExportaciones: form.infoExportaciones,
          paisesDestino: form.paisesDestino,
          productosExportados: form.productosExportados,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || 'No fue posible enviar la solicitud.'
        );
      }

      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'No fue posible enviar la solicitud.'
      );
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-sm">

          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 size={34} />
          </div>

          <h1 className="text-2xl font-800 text-foreground">
            Solicitud enviada
          </h1>

          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Recibimos correctamente tu solicitud de registro como empresa.
            <br />
            Tu información será revisada por el Administrador General.
          </p>

          <p className="mt-4 rounded-xl bg-muted px-4 py-3 text-sm text-foreground">
            Tu solicitud permanecerá <strong>pendiente de revisión</strong>
            hasta que sea aprobada.
          </p>

          <button
            type="button"
            onClick={onBack}
            className="mt-6 w-full rounded-xl bg-primary px-5 py-3 text-sm font-800 text-primary-foreground hover:opacity-90 transition"
          >
            Regresar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-5 py-8 md:px-8">
      <div className="mx-auto max-w-4xl">

        <button
          type="button"
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-sm font-700 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={18} />
          Regresar
        </button>

        <div className="mb-8">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Building2 size={23} />
            </div>

            <div>
              <p className="text-xs font-700 uppercase tracking-wide text-primary">
                Premio Nacional de Exportación
              </p>
              <h1 className="text-2xl font-800 text-foreground">
                Registro de empresa
              </h1>
            </div>
          </div>

          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            Completa la información de tu empresa para solicitar tu
            incorporación al sistema. La solicitud será revisada por el
            Administrador General.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ACCESO */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-800 text-foreground">
              Datos de acceso
            </h2>

            <p className="mt-1 mb-5 text-sm text-muted-foreground">
              Estos datos serán utilizados para ingresar al sistema.
            </p>

            <div className="grid gap-5 md:grid-cols-2">

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Correo electrónico *
                </label>

                <div className="relative">
                  <Mail
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />

                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    className={`${inputClass} pl-10`}
                    placeholder="correo@empresa.com"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  Contraseña *
                </label>

                <div className="relative">
                  <Lock
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />

                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => update('password', e.target.value)}
                    className={`${inputClass} pl-10 pr-11`}
                    placeholder="Mínimo 8 caracteres"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  Confirmar contraseña *
                </label>

                <div className="relative">
                  <Lock
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />

                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={form.confirmPassword}
                    onChange={(e) =>
                      update('confirmPassword', e.target.value)
                    }
                    className={`${inputClass} pl-10 pr-11`}
                    placeholder="Repite tu contraseña"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* EMPRESA */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-800 text-foreground">
              Información de la empresa
            </h2>

            <div className="mt-5 grid gap-5 md:grid-cols-2">

              <div className="md:col-span-2">
                <label className={labelClass}>Razón social *</label>
                <input
                  required
                  value={form.razonSocial}
                  onChange={(e) => update('razonSocial', e.target.value)}
                  className={inputClass}
                  placeholder="Razón social"
                />
              </div>

              <div>
                <label className={labelClass}>Nombre comercial</label>
                <input
                  value={form.nombreComercial}
                  onChange={(e) => update('nombreComercial', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>RFC *</label>
                <input
                  required
                  value={form.rfc}
                  onChange={(e) => update('rfc', e.target.value.toUpperCase())}
                  className={inputClass}
                  placeholder="RFC"
                />
              </div>

              <div>
                <label className={labelClass}>Sector</label>
                <input
                  value={form.sector}
                  onChange={(e) => update('sector', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Categoría del Premio Nacional de Exportación *
                </label>
                <select
                  required
                  value={form.categoryId}
                  onChange={(e) => update('categoryId', e.target.value)}
                  className={inputClass}
                  disabled={categoriesLoading}
                >
                  <option value="">
                    {categoriesLoading
                      ? 'Cargando categorías...'
                      : 'Selecciona una categoría'}
                  </option>

                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.codigo} — {category.nombre}
                    </option>
                  ))}
                </select>

                {!categoriesLoading && categories.length === 0 && (
                  <p className="mt-1.5 text-xs text-destructive">
                    No hay categorías disponibles para el registro.
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>Tamaño de empresa</label>
                <select
                  value={form.tamano}
                  onChange={(e) => update('tamano', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Micro">Micro</option>
                  <option value="Pequeña">Pequeña</option>
                  <option value="Mediana">Mediana</option>
                  <option value="Grande">Grande</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Año de constitución</label>
                <input
                  type="number"
                  value={form.anioConstitucion}
                  onChange={(e) =>
                    update('anioConstitucion', e.target.value)
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Sitio web</label>
                <div className="relative">
                  <Globe2
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="text"
                    value={form.sitioWeb}
                    onChange={(e) => update('sitioWeb', e.target.value)}
                    className={`${inputClass} pl-10`}
                    placeholder="https://"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* DOMICILIO */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-800 text-foreground">
              Domicilio
            </h2>

            <div className="mt-5 grid gap-5 md:grid-cols-2">

              <div className="md:col-span-2">
                <label className={labelClass}>Domicilio</label>
                <input
                  value={form.domicilio}
                  onChange={(e) => update('domicilio', e.target.value)}
                  className={inputClass}
                  placeholder="Calle, número, colonia..."
                />
              </div>

              <div>
                <label className={labelClass}>Estado</label>
                <input
                  value={form.estado}
                  onChange={(e) => update('estado', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Municipio / Alcaldía</label>
                <input
                  value={form.municipio}
                  onChange={(e) => update('municipio', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Código postal</label>
                <input
                  value={form.codigoPostal}
                  onChange={(e) => update('codigoPostal', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>País</label>
                <input
                  value={form.pais}
                  onChange={(e) => update('pais', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          {/* CONTACTO */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-800 text-foreground">
              Persona responsable
            </h2>

            <div className="mt-5 grid gap-5 md:grid-cols-2">

              <div className="md:col-span-2">
                <label className={labelClass}>Nombre completo *</label>
                <div className="relative">
                  <UserRound
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    required
                    value={form.nombreCompleto}
                    onChange={(e) =>
                      update('nombreCompleto', e.target.value)
                    }
                    className={`${inputClass} pl-10`}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Cargo *</label>
                <input
                  required
                  value={form.cargo}
                  onChange={(e) => update('cargo', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Teléfono *</label>
                <input
                  required
                  value={form.telefono}
                  onChange={(e) => update('telefono', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          {/* EXPORTACIONES */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-800 text-foreground">
              Información de exportaciones
            </h2>

            <div className="mt-5 space-y-5">

              <div>
                <label className={labelClass}>
                  Información sobre exportaciones
                </label>
                <textarea
                  rows={4}
                  value={form.infoExportaciones}
                  onChange={(e) =>
                    update('infoExportaciones', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Describe brevemente la experiencia exportadora de la empresa."
                />
              </div>

              <div>
                <label className={labelClass}>
                  Países de destino
                </label>
                <textarea
                  rows={3}
                  value={form.paisesDestino}
                  onChange={(e) =>
                    update('paisesDestino', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Países a los que exporta actualmente."
                />
              </div>

              <div>
                <label className={labelClass}>
                  Productos exportados
                </label>
                <textarea
                  rows={3}
                  value={form.productosExportados}
                  onChange={(e) =>
                    update('productosExportados', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Principales productos o servicios que exporta."
                />
              </div>
            </div>
          </section>

          {/* AVISO */}
          <div className="rounded-xl border border-border bg-muted/40 px-5 py-4 text-sm text-muted-foreground">
            Al enviar esta solicitud, tus datos serán enviados al
            Administrador General para su revisión. El envío de esta
            solicitud <strong>no implica la asignación automática de un rol
            ni la aprobación de la cuenta.</strong>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-primary px-6 py-4 text-base font-800 text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Enviando solicitud...' : 'Enviar solicitud de registro'}
          </button>

        </form>
      </div>
    </div>
  );
}
